package com.daytimegaming.ourdailyfinances.presentation.account

import com.daytimegaming.ourdailyfinances.domain.account.AccountEventBus
import com.daytimegaming.ourdailyfinances.domain.dashboard.DashboardEventBus
import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.daytimegaming.ourdailyfinances.domain.Response
import com.daytimegaming.ourdailyfinances.domain.model.Account
import com.daytimegaming.ourdailyfinances.domain.model.Holding
import com.daytimegaming.ourdailyfinances.domain.model.InvestmentTransaction
import com.daytimegaming.ourdailyfinances.domain.model.PlaidItem
import com.daytimegaming.ourdailyfinances.domain.model.Transaction
import com.daytimegaming.ourdailyfinances.domain.model.SecurityPerformance
import com.daytimegaming.ourdailyfinances.domain.repository.TransactionRepository
import com.daytimegaming.ourdailyfinances.domain.usecase.AccountUseCase
import com.daytimegaming.ourdailyfinances.domain.usecase.DashboardUseCase
import com.daytimegaming.ourdailyfinances.domain.plaid.PlaidItemsEventBus
import com.daytimegaming.ourdailyfinances.domain.usecase.AuthUseCase
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

sealed class AccountDetailScreenState {
    data object Loading : AccountDetailScreenState()
    data class Error(val message: String) : AccountDetailScreenState()
    data class Loaded(
        val account: Account,
        val transactions: List<Transaction>,
        val plaidItem: PlaidItem? = null,
        val holdings: List<Holding> = emptyList(),
        val investmentTransactions: List<InvestmentTransaction> = emptyList(),
        val securityPerformance: SecurityPerformance? = null,
        val isOwner: Boolean = true,
        val isSecurityPerformanceLoading: Boolean = false,
    ) : AccountDetailScreenState()
}

class AccountDetailViewModel(
    private val accountId: String,
    private val dashboardId: String? = null,
    private val accountUseCase: AccountUseCase,
    private val transactionRepository: TransactionRepository,
    private val dashboardUseCase: DashboardUseCase,
    private val plaidItemsEventBus: PlaidItemsEventBus,
    private val authUseCase: AuthUseCase,
    private val accountEventBus: AccountEventBus,
    private val dashboardEventBus: DashboardEventBus,
) : ViewModel() {

    private val _state = MutableStateFlow<AccountDetailScreenState>(AccountDetailScreenState.Loading)
    val state = _state.asStateFlow()

    fun updateNickname(nickname: String?, onSuccess: () -> Unit = {}, onError: (String) -> Unit = {}) {
        viewModelScope.launch {
            try {
                accountUseCase.UpdateAccountNickname(accountId, nickname)
                plaidItemsEventBus.itemsChanged()
                load()
                onSuccess()
            } catch (e: Exception) {
                println("ERROR: updateNickname failed: $e")
                e.printStackTrace()
                onError(e.message ?: "Failed to update nickname")
            }
        }
    }

    private val _securityPerformance = MutableStateFlow<SecurityPerformance?>(null)
    private val _isSecurityPerformanceLoading = MutableStateFlow(false)
    private var hasLoadedSecurityPerformance = false

    private fun checkAndLoadSecurityPerformance(accountType: String): Boolean {
        if (accountType.lowercase() == "investment" && !hasLoadedSecurityPerformance) {
            hasLoadedSecurityPerformance = true
            _isSecurityPerformanceLoading.value = true
            viewModelScope.launch {
                try {
                    val response = transactionRepository.getSecurityPerformance(
                        dashboardId = dashboardId,
                        accountId = if (dashboardId == null) accountId else null
                    )
                    if (response is Response.Success) {
                        _securityPerformance.value = response.data
                    }
                } catch (e: Exception) {
                    println("ERROR: getSecurityPerformance failed: $e")
                } finally {
                    _isSecurityPerformanceLoading.value = false
                }
            }
            return true
        }
        return false
    }

    init {
        load()
        viewModelScope.launch {
            accountEventBus.events.collect { updatedAccountId ->
                if (updatedAccountId == accountId) {
                    load()
                }
            }
        }
        viewModelScope.launch {
            dashboardEventBus.events.collect { updatedDashboardId ->
                if (dashboardId != null && updatedDashboardId == dashboardId) {
                    load()
                }
            }
        }
    }

    fun load() {
        if (dashboardId != null) {
            viewModelScope.launch {
                transactionRepository.refresh()
            }
            viewModelScope.launch {
                combine(
                    dashboardUseCase.GetDashboardDetail(dashboardId),
                    accountUseCase.GetPlaidItems(),
                    transactionRepository.investments,
                    combine(_securityPerformance, _isSecurityPerformanceLoading) { sec, loading -> sec to loading },
                    combine(authUseCase.GetCurrentUser(), transactionRepository.transactions) { user, txs -> user to txs },
                ) { dashboardResponse, plaidItemsResponse, investmentsResponse, (securityPerformance, isSecurityPerformanceLoading), (currentUser, transactionsResponse) ->
                    when (dashboardResponse) {
                        is Response.Error -> AccountDetailScreenState.Error(dashboardResponse.message)
                        is Response.Success -> {
                            val detail = dashboardResponse.data
                            val dbAccount = detail.accounts.find { it.accountId == accountId }
                                ?: return@combine AccountDetailScreenState.Error("Account not found")
                            val account = Account(
                                accountId = dbAccount.accountId,
                                name = dbAccount.name,
                                officialName = dbAccount.officialName,
                                type = dbAccount.type,
                                subtype = dbAccount.subtype,
                                currentBalance = dbAccount.currentBalance,
                                availableBalance = dbAccount.availableBalance,
                                isoCurrencyCode = dbAccount.isoCurrencyCode,
                                institutionName = dbAccount.institutionName,
                                itemId = dbAccount.itemId,
                                nickname = dbAccount.nickname
                            )
                            val triggered = checkAndLoadSecurityPerformance(account.type)
                            val isLoadingPerf = if (triggered) true else isSecurityPerformanceLoading
                            val transactionsFromDetail = detail.transactions.filter { it.accountId == accountId }
                            val transactionsFromRepo = if (transactionsResponse is Response.Success) {
                                transactionsResponse.data.filter { it.accountId == accountId }
                            } else {
                                emptyList()
                            }
                            val transactions = (transactionsFromDetail + transactionsFromRepo)
                                .distinctBy { it.transactionId }
                                .sortedByDescending { it.date }
                            val plaidItem = if (plaidItemsResponse is Response.Success) {
                                plaidItemsResponse.data.find { it.itemId == account.itemId }
                            } else null
                            val mappedAccount = account.copy(institutionName = plaidItem?.institutionName ?: account.institutionName)

                            val dashboardInvestments = detail.investments ?: (investmentsResponse as? Response.Success)?.data
                            val holdings = dashboardInvestments?.holdings?.filter { it.accountId == accountId } ?: emptyList()
                            val investmentTransactions = dashboardInvestments?.transactions?.filter { it.accountId == accountId } ?: emptyList()

                            AccountDetailScreenState.Loaded(
                                account = mappedAccount,
                                transactions = transactions,
                                plaidItem = plaidItem,
                                holdings = holdings,
                                investmentTransactions = investmentTransactions,
                                securityPerformance = securityPerformance,
                                isOwner = currentUser?.uid == dbAccount.addedByUserId,
                                isSecurityPerformanceLoading = isLoadingPerf,
                            )
                        }

                        else -> AccountDetailScreenState.Loading
                    }
                }.collect { nextState ->
                    _state.update { s ->
                        if (s is AccountDetailScreenState.Loaded) {
                            nextState as? AccountDetailScreenState.Loaded ?: s
                        } else {
                            nextState
                        }
                    }
                }
            }
        } else {
            viewModelScope.launch {
                transactionRepository.refresh()
            }
            viewModelScope.launch {
                combine(
                    accountUseCase.GetAccounts(),
                    accountUseCase.GetPlaidItems(),
                    transactionRepository.transactions,
                    transactionRepository.investments,
                    combine(_securityPerformance, _isSecurityPerformanceLoading) { sec, loading -> sec to loading },
                ) { accountsResponse, plaidItemsResponse, transactionsResponse, investmentsResponse, (securityPerformance, isSecurityPerformanceLoading) ->
                    when {
                        accountsResponse is Response.Error ->
                            AccountDetailScreenState.Error(accountsResponse.message)
                        transactionsResponse is Response.Error ->
                            AccountDetailScreenState.Error(transactionsResponse.message)
                        accountsResponse is Response.Success && transactionsResponse is Response.Success -> {
                            val account = accountsResponse.data.find { it.accountId == accountId }
                                ?: return@combine AccountDetailScreenState.Error("Account not found")
                            val triggered = checkAndLoadSecurityPerformance(account.type)
                            val isLoadingPerf = if (triggered) true else isSecurityPerformanceLoading
                            val filtered = transactionsResponse.data.filter { it.accountId == accountId }
                            val plaidItem = if (plaidItemsResponse is Response.Success) {
                                plaidItemsResponse.data.find { it.itemId == account.itemId }
                            } else null
                            val mappedAccount = account.copy(institutionName = plaidItem?.institutionName)

                            val holdings = if (investmentsResponse is Response.Success) {
                                investmentsResponse.data?.holdings?.filter { it.accountId == accountId } ?: emptyList()
                            } else {
                                emptyList()
                            }
                            val investmentTransactions = if (investmentsResponse is Response.Success) {
                                investmentsResponse.data?.transactions?.filter { it.accountId == accountId } ?: emptyList()
                            } else {
                                emptyList()
                            }

                            AccountDetailScreenState.Loaded(
                                account = mappedAccount,
                                transactions = filtered,
                                plaidItem = plaidItem,
                                holdings = holdings,
                                investmentTransactions = investmentTransactions,
                                securityPerformance = securityPerformance,
                                isOwner = true,
                                isSecurityPerformanceLoading = isLoadingPerf,
                            )
                        }
                        else -> AccountDetailScreenState.Loading
                    }
                }.collect { nextState ->
                    _state.update { s ->
                        if (s is AccountDetailScreenState.Loaded) {
                            nextState as? AccountDetailScreenState.Loaded ?: s
                        } else {
                            nextState
                        }
                    }
                }
            }
        }
    }
}
